import {
	ASTEROID_MASS,
	ASTEROID_RADIUS,
	ASTEROID_SIZE_SCALE,
	ASTEROID_STRENGTH,
	FUEL_MAX,
	LASER_MASS,
	LASER_RADIUS,
	MISSILE_MASS,
	MISSILE_RADIUS,
	MISSILE_STRENGTH,
	PICKUP_RADIUS,
	SHIP_MASS,
	SHIP_RADIUS,
	SHIP_STRENGTH,
	SPAWN_POINT_RADIUS,
} from "./constants";
import type { PowerUpKind } from "./match-rules";
import type { Vec2 } from "./math/vec2";

export type EntityId = number;
export type PlayerIndex = number;
export type PlanetType = "mars" | "earth" | "pluto";
/** Player colours in player order (P11.3 added green and yellow for 3–4 player matches). */
export type ShipColor = "blue" | "red" | "green" | "yellow";

/** Fields shared by every world object. All entities are plain, serialisable data. */
interface EntityBase {
	id: EntityId;
	pos: Vec2;
	vel: Vec2;
	/** Orientation in radians (0 = +x, increasing clockwise on screen since y is down). */
	angle: number;
	radius: number;
	mass: number;
}

export interface Ship extends EntityBase {
	kind: "ship";
	player: PlayerIndex;
	color: ShipColor;
	/** The engine is burning this tick (thrust held and, with the fuel rule, fuel left). */
	thrusting: boolean;
	/** Thrust input as held by the player (fuel refills only while this is released). */
	thrustHeld: boolean;
	/** Remaining fuel in ticks of thrust (used by the fuel rule). */
	fuel: number;
	/** Laser heat (overheat rule): 0 = cold, rules.laserHeatMax = overheated. */
	laserHeat: number;
	/** Locked out after reaching max heat, until the laser is cold again. */
	laserOverheated: boolean;
	/** Missiles carried (when rules.missilesPerLife limits them). */
	missiles: number;
	/** Remaining hull (hull-damage rule). */
	hull: number;
	/** -1 = turning left, 1 = turning right, 0 = not turning. */
	turning: -1 | 0 | 1;
	firingLaser: boolean;
	firingMissile: boolean;
	/** Ticks of charge accumulated, capped at the weapon's cooldown. */
	laserCharge: number;
	missileCharge: number;
	// Power-up effects (P8.7). They live on the ship, so they end when it is destroyed.
	/** Ticks of triple laser left. */
	tripleLaserTicks: number;
	/** Ticks of warp speed left. */
	warpTicks: number;
	/** Laser hits the shield can still absorb (0 = no shield). */
	shield: number;
	/** Ticks the ship stays frozen (no thrust, turning or firing). */
	frozenTicks: number;
	/** Homing missiles left to fire. */
	homingShots: number;
}

export interface Planet extends EntityBase {
	kind: "planet";
	planetType: PlanetType;
	/** Mass at the start of the match (growing planets rule, P11.7). */
	baseMass: number;
}

export interface Asteroid extends EntityBase {
	kind: "asteroid";
	/** 0 = full, 1 = half, 2 = quarter size (asteroid splitting rule, P11.6). */
	size: number;
	/** Rotation per tick. */
	spin: number;
	hull: number;
}

export interface Laser extends EntityBase {
	kind: "laser";
	owner: PlayerIndex;
	/** Ticks until expiry, or null for no expiry. */
	ticksLeft: number | null;
	/** Distance left to fly (laser-range rule), or null for the tick lifetime. */
	rangeLeft: number | null;
}

export interface Missile extends EntityBase {
	kind: "missile";
	owner: PlayerIndex;
	ticksLeft: number | null;
	hull: number;
	/** Ticks of homing guidance left (0 = a plain, ballistic missile). */
	homingTicks: number;
	/** The entity a homing missile is chasing, if any. */
	targetId: EntityId | null;
}

export interface SpawnPoint extends EntityBase {
	kind: "spawnPoint";
	/** True while any movable object overlaps it (recomputed every collision pass). */
	occupied: boolean;
}

export type PickupItem = PowerUpKind;

/** A static power-up pod (grew out of the legacy FuelItem / MissileItem). */
export interface Pickup extends EntityBase {
	kind: "pickup";
	item: PickupItem;
	ticksLeft: number;
}

export type Entity = Ship | Planet | Asteroid | Laser | Missile | SpawnPoint | Pickup;
export type EntityKind = Entity["kind"];
export type Movable = Ship | Asteroid | Laser | Missile;
export type Projectile = Laser | Missile;
/** Legacy DestroyableObject subclasses: destroyed by projectiles and planets. */
export type Destroyable = Ship | Asteroid | Missile;

export function isMovable(e: Entity): e is Movable {
	return e.kind === "ship" || e.kind === "asteroid" || e.kind === "laser" || e.kind === "missile";
}

export function isProjectile(e: Entity): e is Projectile {
	return e.kind === "laser" || e.kind === "missile";
}

export function isDestroyable(e: Entity): e is Destroyable {
	return e.kind === "ship" || e.kind === "asteroid" || e.kind === "missile";
}

// --- Factories -------------------------------------------------------------

function base(id: EntityId, pos: Vec2, radius: number, mass: number): EntityBase {
	return { id, pos: { x: pos.x, y: pos.y }, vel: { x: 0, y: 0 }, angle: 0, radius, mass };
}

export function createShip(id: EntityId, player: PlayerIndex, color: ShipColor, pos: Vec2): Ship {
	return {
		...base(id, pos, SHIP_RADIUS, SHIP_MASS),
		kind: "ship",
		player,
		color,
		thrusting: false,
		thrustHeld: false,
		fuel: FUEL_MAX,
		laserHeat: 0,
		laserOverheated: false,
		missiles: 0,
		hull: SHIP_STRENGTH,
		turning: 0,
		firingLaser: false,
		firingMissile: false,
		laserCharge: 0,
		missileCharge: 0,
		tripleLaserTicks: 0,
		warpTicks: 0,
		shield: 0,
		frozenTicks: 0,
		homingShots: 0,
	};
}

export function createPlanet(
	id: EntityId,
	planetType: PlanetType,
	pos: Vec2,
	radius: number,
	mass: number,
): Planet {
	return { ...base(id, pos, radius, mass), kind: "planet", planetType, baseMass: mass };
}

export function createAsteroid(
	id: EntityId,
	pos: Vec2,
	vel: Vec2,
	spin: number,
	size = 0,
): Asteroid {
	const scale = ASTEROID_SIZE_SCALE[size] ?? 1;
	return {
		...base(id, pos, ASTEROID_RADIUS * scale, ASTEROID_MASS * scale),
		kind: "asteroid",
		size,
		vel: { x: vel.x, y: vel.y },
		spin,
		hull: Math.max(1, Math.round(ASTEROID_STRENGTH * scale)),
	};
}

export function createLaser(
	id: EntityId,
	owner: PlayerIndex,
	pos: Vec2,
	vel: Vec2,
	angle: number,
	ticksLeft: number | null,
	rangeLeft: number | null = null,
): Laser {
	return {
		...base(id, pos, LASER_RADIUS, LASER_MASS),
		kind: "laser",
		vel: { x: vel.x, y: vel.y },
		angle,
		owner,
		ticksLeft,
		rangeLeft,
	};
}

export function createMissile(
	id: EntityId,
	owner: PlayerIndex,
	pos: Vec2,
	vel: Vec2,
	angle: number,
	ticksLeft: number | null,
): Missile {
	return {
		...base(id, pos, MISSILE_RADIUS, MISSILE_MASS),
		kind: "missile",
		vel: { x: vel.x, y: vel.y },
		angle,
		owner,
		ticksLeft,
		hull: MISSILE_STRENGTH,
		homingTicks: 0,
		targetId: null,
	};
}

export function createPickup(id: EntityId, item: PickupItem, pos: Vec2, ticksLeft: number): Pickup {
	return { ...base(id, pos, PICKUP_RADIUS, 0), kind: "pickup", item, ticksLeft };
}

export function createSpawnPoint(id: EntityId, pos: Vec2): SpawnPoint {
	return { ...base(id, pos, SPAWN_POINT_RADIUS, 0), kind: "spawnPoint", occupied: false };
}
